import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of, from } from 'rxjs';
import { concatMap, toArray, delay } from 'rxjs/operators';
import { StateMachineService } from '../../../../services/state-machine.service';
import { GenericFormComponent } from '../../../../shared/components/generic-crud/generic-form/generic-form.component';
import { GenericForm, GenericFormField } from '../../../../models/GenericCRUD/GenericForm';
import { ConfirmationComponent } from '../../../../shared/components/confirmation/confirmation.component';
import { ProductService } from '../../../../services/product.service';
import { CustomDropdownComponent } from '../../../../shared/components/form-controls/custom-dropdown/custom-dropdown.component';
import { Product } from '../../../../models/Product';

@Component({
  selector: 'app-product-transitions',
  standalone: true,
  imports: [CommonModule, FormsModule, GenericFormComponent, ConfirmationComponent, CustomDropdownComponent],
  templateUrl: './product-transitions.component.html',
  styleUrl: './product-transitions.component.css'
})
export class ProductTransitionsComponent implements OnInit {

  @Input() productId!: string;

  transitions: any[] = [];
  groupedTransitions: { [key: string]: any[] } = {};
  fromStates: any[] = [];
  loading = false;
  error = '';

  // Vista
  viewMode: 'flow' | 'list' = 'flow';
  expandedStates: { [key: number]: boolean } = {};
  allStates: any[] = [];
  stateDisplayOrder: Map<number, number> = new Map(); // Orden de visualización de estados

  // Formulario
  showForm = false;
  transitionForm?: GenericForm;
  selectedTransition: any = null;

  // Modal de confirmación
  showDeleteConfirmation = false;
  transitionToDelete: number | null = null;

  // Modal de copia de transiciones
  showCopyModal = false;
  availableProducts: Product[] = [];
  copyProductsLoading = false;
  copyLoading = false;
  copyError = '';
  selectedSourceProductId: number | null = null;

  // Drag & Drop
  draggedState: any = null;
  draggedOverState: any = null;
  isReordering = false; // Para mostrar loading sin recargar
  autoScrollInterval: any = null; // Para auto-scroll durante drag
  showSuccessToast = false; // Toast de éxito
  successMessage = ''; // Mensaje del toast
  private usedColors: { [key: number]: string } = {}; // Colores asignados por estado

  constructor(
    private stateMachineService: StateMachineService,
    private productService: ProductService
  ) { }

  ngOnInit(): void {
    if (this.productId) {
      this.loadTransitions();
      this.initializeForm();
    }
  }

  initializeForm(): void {
    const formFields: GenericFormField[] = [
      {
        name: 'from_state_id',
        type: 'select',
        label: 'Estado Origen (opcional)',
        validations: 'nullable',
        url: `/state-machine/transitions/helpers/states`,
        singleSelection: true,
        labelKey: 'label'
      },
      {
        name: 'to_state_id',
        type: 'select',
        label: 'Estado Destino',
        validations: 'required',
        url: `/state-machine/transitions/helpers/states`,
        singleSelection: true,
        labelKey: 'label'
      },
      {
        name: 'trigger_event',
        type: 'select',
        label: 'Evento Disparador',
        validations: 'required',
        url: '/state-machine/transitions/helpers/trigger-events',
        singleSelection: true,
        labelKey: 'label',
        valueKey: 'value'
      },
      {
        name: 'condition_ids',
        type: 'select',
        label: 'Condiciones',
        validations: 'nullable',
        url: '/state-machine/conditions',
        singleSelection: false,
        labelKey: 'name',
        colspan: 1
      },
      {
        name: 'action_ids',
        type: 'select',
        label: 'Acciones',
        validations: 'nullable',
        url: '/state-machine/actions',
        singleSelection: false,
        labelKey: 'name',
        colspan: 1
      },
      {
        name: 'rate_id',
        type: 'select',
        label: 'Tarifa a Cobrar',
        validations: 'nullable',
        url: `/product/${this.productId}/price-list`,
        singleSelection: true,
        labelKey: 'label',
        valueKey: 'value',
        colspan: 1,
        showIf: (item?: any) => {
          // Mostrar solo si se ha seleccionado la acción "charge_product_rate"
          if (!item || !item.action_ids) return false;
          
          const actions = Array.isArray(item.action_ids) ? item.action_ids : [];
          return actions.some((action: any) => {
            // Buscar si alguna acción tiene el code 'charge_product_rate'
            return action.code === 'charge_product_rate' || action.name?.includes('Cobrar tarifa');
          });
        }
      }
    ];

    this.transitionForm = {
      successTitle: 'Transición guardada correctamente',
      errorTitle: 'Error al guardar transición',
      method: 'POST',
      fields: formFields,
      name: 'transicion',
      createButtonTitle: '',
      cols: 2,
      createApiSlug: `/state-machine/transitions?product_id=${this.productId}`,
      originalUpdateApiSlug: `/state-machine/transitions/{{id}}`,
      formTitle: 'Nueva Transición',
      formButtonTitle: 'Crear',
      width: '4xl'
    };
  }

  /**
   * Carga las transiciones desde el backend
   * @param preserveOrder Si es true, mantiene el orden visual actual de los estados
   */
  loadTransitions(preserveOrder: boolean = false): void {
    this.loading = true;
    this.error = '';

    // Guardar el orden visual actual si se solicita preservarlo
    const savedStateOrder = preserveOrder ? [...this.allStates] : null;

    this.stateMachineService.getTransitions({ product_id: Number(this.productId) }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.transitions = response.data.sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
          this.extractAllStates();

          // Si queremos preservar el orden, restaurarlo; sino, recalcular
          if (preserveOrder && savedStateOrder && savedStateOrder.length > 0) {
            this.restoreStateOrder(savedStateOrder);
          } else {
            this.sortStatesByFlow();
          }
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Error al cargar las transiciones';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  /**
   * Restaura el orden visual de los estados basándose en un orden previo guardado
   */
  private restoreStateOrder(savedStateOrder: any[]): void {
    const stateIdOrder = new Map(savedStateOrder.map((state, index) => [state.id, index]));

    this.allStates.sort((a, b) => {
      const orderA = stateIdOrder.get(a.id) ?? 999999;
      const orderB = stateIdOrder.get(b.id) ?? 999999;
      return orderA - orderB;
    });

    // Actualizar el mapa de orden de visualización
    this.stateDisplayOrder.clear();
    this.allStates.forEach((state, index) => {
      this.stateDisplayOrder.set(state.id, index);
    });
  }

  extractAllStates(): void {
    const statesMap = new Map<number, any>();

    // Extraer todos los estados únicos de las transiciones
    this.transitions.forEach(t => {
      if (t.from_state && !statesMap.has(t.from_state.id)) {
        statesMap.set(t.from_state.id, t.from_state);
      }
      if (t.to_state && !statesMap.has(t.to_state.id)) {
        statesMap.set(t.to_state.id, t.to_state);
      }
    });

    this.allStates = Array.from(statesMap.values());
  }

  /**
   * Ordena los estados basándose en el campo 'order' de las transiciones entrantes
   * y actualiza el mapa de orden de visualización
   */
  private sortStatesByFlow(): void {
    if (this.transitions.length === 0 || this.allStates.length === 0) {
      return;
    }

    // Crear un mapa de estado -> order mínimo de sus transiciones entrantes
    const stateOrderMap = new Map<number, number>();

    this.allStates.forEach(state => {
      // Obtener transiciones que van HACIA este estado
      const incomingTransitions = this.transitions.filter(t => t.to_state_id === state.id);

      if (incomingTransitions.length > 0) {
        // Usar el order mínimo de las transiciones entrantes
        const minOrder = Math.min(...incomingTransitions.map(t => t.order || 0));
        stateOrderMap.set(state.id, minOrder);
      } else {
        // Estados sin transiciones entrantes (estados iniciales del flujo)
        // Buscar si tiene transiciones salientes para determinar su posición
        const outgoingTransitions = this.transitions.filter(t => t.from_state_id === state.id);

        if (outgoingTransitions.length > 0) {
          // Es un estado inicial: usar order -1 para ponerlo al principio
          stateOrderMap.set(state.id, -1);
        } else {
          // Estado huérfano sin transiciones: ponerlo al final
          stateOrderMap.set(state.id, 999999);
        }
      }
    });

    // Ordenar estados por el order
    this.allStates.sort((a, b) => {
      const orderA = stateOrderMap.get(a.id) ?? 999999;
      const orderB = stateOrderMap.get(b.id) ?? 999999;
      return orderA - orderB;
    });

    // Actualizar el mapa de orden de visualización
    this.stateDisplayOrder.clear();
    this.allStates.forEach((state, index) => {
      this.stateDisplayOrder.set(state.id, index);
    });

  }

  getTransitionsFrom(stateId: number | null): any[] {
    const transitions = this.transitions.filter(t =>
      (stateId === null && !t.from_state) ||
      (t.from_state && t.from_state.id === stateId)
    );

    // Ordenar transiciones: exitosas primero, luego fallidas
    return transitions.sort((a, b) => {
      const aSuccess = this.isTransitionSuccess(a);
      const bSuccess = this.isTransitionSuccess(b);

      if (aSuccess === true && bSuccess !== true) return -1;
      if (bSuccess === true && aSuccess !== true) return 1;
      if (aSuccess === false && bSuccess === null) return 1;
      if (bSuccess === false && aSuccess === null) return -1;

      return 0;
    });
  }

  getStateName(stateId: number | null): string {
    if (stateId === null) return 'Inicial';
    const state = this.allStates.find(s => s.id === stateId);
    return state?.name || 'Desconocido';
  }

  getStateColor(stateId: number | null): string {
    if (stateId === null) return 'bg-gray-100 border-gray-300';

    // Si ya tiene un color asignado, lo devolvemos
    if (this.usedColors[stateId]) {
      return this.usedColors[stateId];
    }

    const colors = [
      'bg-blue-100 border-blue-400',      // Azul claro
      'bg-green-100 border-green-400',    // Verde menta
      'bg-yellow-100 border-yellow-400',  // Amarillo suave
      'bg-orange-100 border-orange-400',  // Naranja pastel
      'bg-red-100 border-red-400',        // Rojo coral
      'bg-purple-100 border-purple-400',  // Púrpura lavanda
      'bg-pink-100 border-pink-400',      // Rosa chicle
      'bg-teal-100 border-teal-400',      // Turquesa
      'bg-cyan-100 border-cyan-400',      // Cian claro
      'bg-indigo-100 border-indigo-400',  // Índigo
      'bg-violet-100 border-violet-400',  // Violeta
      'bg-fuchsia-100 border-fuchsia-400',// Fucsia
      'bg-rose-100 border-rose-400',      // Rosa intenso
      'bg-amber-100 border-amber-400',    // Ámbar
      'bg-lime-100 border-lime-400',      // Lima
      'bg-emerald-100 border-emerald-400',// Esmeralda
    ];

    // Encontrar el primer color no usado
    const availableColor = colors.find(color =>
      !Object.values(this.usedColors).includes(color)
    );

    // Si no hay colores disponibles, empezar a reciclar
    const selectedColor = availableColor || colors[Object.keys(this.usedColors).length % colors.length];

    // Guardar el color asignado
    this.usedColors[stateId] = selectedColor;

    return selectedColor;
  }

  toggleExpanded(stateId: number): void {
    this.expandedStates[stateId] = !this.expandedStates[stateId];
  }

  resetStateColors(): void {
    this.usedColors = {};
  }

  isTransitionSuccess(transition: any): boolean | null {
    const trigger = transition.trigger_event?.toLowerCase() || '';
    const label = transition.trigger_event_label?.toLowerCase() || '';
    const combined = `${trigger} ${label}`;

    // Transiciones exitosas
    if (combined.includes('passed') ||
      combined.includes('aprobado') ||
      combined.includes('approved') ||
      combined.includes('finished') ||
      combined.includes('finalizado')) {
      return true;
    }

    // Transiciones fallidas
    if (combined.includes('failed') ||
      combined.includes('suspendido') ||
      combined.includes('expired') ||
      combined.includes('caducado')) {
      return false;
    }

    return null;
  }

  deleteTransition(id: number): void {
    this.transitionToDelete = id;
    this.showDeleteConfirmation = true;
  }

  confirmDelete(): void {
    if (this.transitionToDelete === null) return;

    this.stateMachineService.deleteTransition(this.transitionToDelete).subscribe({
      next: (response) => {
        if (response.success) {
          // Preservar el orden visual al eliminar transiciones individuales
          this.loadTransitions(true);
        }
        this.showDeleteConfirmation = false;
        this.transitionToDelete = null;
      },
      error: (err) => {
        console.error('Error al eliminar:', err);
        this.showDeleteConfirmation = false;
        this.transitionToDelete = null;
      }
    });
  }

  cancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.transitionToDelete = null;
  }

  openCreateForm(): void {
    this.selectedTransition = null;
    if (this.transitionForm) {
      this.transitionForm.method = 'POST';
      this.transitionForm.formButtonTitle = 'Crear';
    }
    this.showForm = true;
  }

  copyTransitionsFromProduct(sourceProductId: number): void {
    if (!sourceProductId || !this.productId) {
      this.copyError = 'Producto origen no válido.';
      return;
    }

    this.copyLoading = true;
    this.copyError = '';

    this.stateMachineService.getTransitions({ product_id: Number(this.productId) }).subscribe({
      next: (currentTransitionsResponse) => {
        const currentTransitions = currentTransitionsResponse.data || [];

        const deleteRequests = currentTransitions.map((t: any) =>
          this.stateMachineService.deleteTransition(t.id)
        );

        const deleteObservable = deleteRequests.length > 0
          ? forkJoin(deleteRequests)
          : of([]);

        deleteObservable.subscribe({
          next: () => {

            this.stateMachineService.getTransitions({ product_id: sourceProductId }).subscribe({
              next: (response) => {
                if (!response.success) {
                  this.copyError = 'Error al obtener las transiciones del producto origen.';
                  this.copyLoading = false;
                  return;
                }

                if (!response.data || response.data.length === 0) {
                  this.copyError = 'El producto origen no tiene transiciones configuradas.';
                  this.copyLoading = false;
                  return;
                }

                const sourceTransitions = response.data;

                const transitionsToCreate = sourceTransitions.map((t: any) => {
                  const conditionDefinitions = t.conditionDefinitions || t.condition_definitions || [];
                  const actionDefinitions = t.actionDefinitions || t.action_definitions || [];

                  const conditionIds = conditionDefinitions
                    .map((c: any) => {
                      const id = typeof c === 'object' ? c.id : c;
                      return id != null ? { id: Number(id) } : null;
                    })
                    .filter((item: any) => item != null);

                  const actionIds = actionDefinitions
                    .map((a: any) => {
                      const id = typeof a === 'object' ? a.id : a;
                      return id != null ? { id: Number(id) } : null;
                    })
                    .filter((item: any) => item != null);

                  const payload = {
                    from_state_id: t.from_state_id,
                    to_state_id: t.to_state_id,
                    trigger_event: t.trigger_event,
                    product_id: Number(this.productId),
                    office_id: t.office_id,
                    is_automatic: !!t.is_automatic,
                    is_active: !!t.is_active,
                    condition_ids: conditionIds,
                    action_ids: actionIds,
                    order: t.order || 0
                  };

                  return payload;
                });

                const createRequests = transitionsToCreate.map((tr) =>
                  this.stateMachineService.createTransition(tr)
                );

                if (createRequests.length === 0) {
                  this.copyError = 'No hay transiciones válidas para copiar.';
                  this.copyLoading = false;
                  return;
                }

                forkJoin(createRequests).subscribe({
                  next: (results) => {
                    const successCount = results.filter(r => r.success).length;

                    this.copyLoading = false;
                    this.closeCopyModal();
                    this.loadTransitions();
                  },
                  error: (err) => {
                    console.error('Error copiando transiciones:', err);
                    this.copyError = err.error?.message || 'Error al copiar las transiciones. Intente nuevamente.';
                    this.copyLoading = false;
                  }
                });
              },
              error: (err) => {
                console.error('Error obteniendo transiciones fuente:', err);
                this.copyError = err.error?.message || 'Error al obtener las transiciones del producto origen.';
                this.copyLoading = false;
              }
            });
          },
          error: (err: any) => {
            console.error('Error eliminando transiciones existentes:', err);
            this.copyError = 'Error al eliminar las transiciones existentes. Intente nuevamente.';
            this.copyLoading = false;
          }
        });
      },
      error: (err) => {
        console.error('Error obteniendo transiciones actuales:', err);
        this.copyError = 'Error al obtener las transiciones actuales del producto.';
        this.copyLoading = false;
      }
    });
  }

  openCopyForm(): void {
    this.copyError = '';
    this.selectedSourceProductId = null;
    this.availableProducts = [];
    this.showCopyModal = true;
    this.loadProductsForCopy();
  }

  closeCopyModal(): void {
    if (this.copyLoading) {
      return;
    }
    this.showCopyModal = false;
    this.selectedSourceProductId = null;
    this.copyLoading = false;
    this.copyError = '';
    this.availableProducts = [];
  }

  confirmCopyTransitions(): void {
    if (!this.selectedSourceProductId) {
      this.copyError = 'Por favor selecciona un producto origen.';
      return;
    }

    if (this.copyLoading) {
      return;
    }

    this.copyTransitionsFromProduct(this.selectedSourceProductId);
  }

  private loadProductsForCopy(): void {
    this.copyProductsLoading = true;
    this.copyError = '';

    this.productService.listAll().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const data: any = response.data;
          let products: Product[] = [];

          if (Array.isArray(data)) {
            products = data;
          } else if (data.data && Array.isArray(data.data)) {
            products = data.data;
          }

          this.availableProducts = products.filter(
            (product: Product) => product.id !== Number(this.productId)
          );

          if (this.availableProducts.length === 0) {
            this.copyError = '';
          }
        } else {
          this.copyError = 'No se pudieron cargar los productos disponibles.';
          this.availableProducts = [];
        }
        this.copyProductsLoading = false;
      },
      error: (err) => {
        console.error('Error cargando productos:', err);
        this.copyError = err.error?.message || 'Error al cargar la lista de productos.';
        this.availableProducts = [];
        this.copyProductsLoading = false;
      }
    });
  }

  openCreateTransitionModal(fromState: any): void {
    // Resetear la transición seleccionada
    this.selectedTransition = null;

    // Preseleccionar el estado de origen con is_automatic e is_active en true
    this.selectedTransition = {
      from_state_id: fromState.id,
      to_state_id: null,
      trigger_event: '',
      trigger_event_label: '',
      is_automatic: 1,
      is_active: 1,
      order: 0,
      condition_ids: [],
      action_ids: []
    };

    // Abrir el modal
    this.showForm = true;
  }

  openEditForm(transition: any): void {
    let conditions: any[] = [];
    const conditionDefs = transition.conditionDefinitions || transition.condition_definitions || [];

    if (Array.isArray(conditionDefs) && conditionDefs.length > 0) {
      conditions = conditionDefs.map((c: any) => ({
        id: c.id,
        name: c.name
      }));
    }

    let actions: any[] = [];
    let rateId: number | null = null;
    const actionDefs = transition.actionDefinitions || transition.action_definitions || [];
    if (Array.isArray(actionDefs) && actionDefs.length > 0) {
      actions = actionDefs.map((a: any) => {
        // Extraer rate_id si la acción es charge_product_rate
        if (a.code === 'charge_product_rate' && a.pivot?.parameters) {
          try {
            const params = typeof a.pivot.parameters === 'string' 
              ? JSON.parse(a.pivot.parameters) 
              : a.pivot.parameters;
            if (params.rate_id) {
              rateId = params.rate_id;
            }
          } catch (e) {
            console.error('Error parsing action parameters:', e);
          }
        }
        return {
          id: a.id,
          name: a.name,
          code: a.code
        };
      });
    }

    this.selectedTransition = {
      ...transition,
      is_automatic: transition.is_automatic ? 1 : 0,
      is_active: transition.is_active ? 1 : 0,
      order: transition.order || 0,
      condition_ids: conditions,
      action_ids: actions,
      rate_id: rateId
    };

    if (this.transitionForm) {
      this.transitionForm.method = 'PUT';
      this.transitionForm.formTitle = 'Editar Transición';
      this.transitionForm.formButtonTitle = 'Actualizar';
      this.transitionForm.updateApiSlug = `/state-machine/transitions/${transition.id}`;
    }
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
    this.selectedTransition = null;
  }

  onFormFinished(): void {
    this.closeForm();
    // Preservar el orden visual al crear/editar transiciones individuales
    this.loadTransitions(true);
  }

  // DRAG & DROP - Reordenar Estados

  onStateDragStart(event: DragEvent, state: any): void {
    this.draggedState = state;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', '');
    }
  }

  onStateDragOver(event: DragEvent, state: any): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.draggedOverState = state;

    // Auto-scroll cuando el mouse está cerca del borde
    this.handleAutoScroll(event);
  }

  private handleAutoScroll(event: DragEvent): void {
    const scrollThreshold = 100; // Píxeles desde el borde para activar scroll
    const scrollSpeed = 10; // Velocidad de scroll

    const viewportHeight = window.innerHeight;
    const mouseY = event.clientY;

    // Scroll hacia arriba
    if (mouseY < scrollThreshold) {
      if (!this.autoScrollInterval) {
        this.autoScrollInterval = setInterval(() => {
          window.scrollBy(0, -scrollSpeed);
        }, 16); // ~60fps
      }
    }
    // Scroll hacia abajo
    else if (mouseY > viewportHeight - scrollThreshold) {
      if (!this.autoScrollInterval) {
        this.autoScrollInterval = setInterval(() => {
          window.scrollBy(0, scrollSpeed);
        }, 16);
      }
    }
    // Detener scroll
    else {
      this.stopAutoScroll();
    }
  }

  private stopAutoScroll(): void {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  onStateDragLeave(event: DragEvent): void {
    this.draggedOverState = null;
  }

  onStateDrop(event: DragEvent, targetState: any): void {
    event.preventDefault();

    if (!this.draggedState || !targetState || this.draggedState.id === targetState.id) {
      return;
    }

    // Encontrar índices en el array de estados
    const draggedIndex = this.allStates.findIndex(s => s.id === this.draggedState.id);
    const targetIndex = this.allStates.findIndex(s => s.id === targetState.id);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Reordenar el array de estados localmente
    const [removed] = this.allStates.splice(draggedIndex, 1);
    this.allStates.splice(targetIndex, 0, removed);

    // Actualizar el order de TODAS las transiciones basándose en el nuevo orden de estados
    const transitionsToUpdate: Array<{ id: number; order: number }> = [];

    // Estrategia: Asignar order basado en la posición del estado DESTINO
    // Para cada estado en su nueva posición, actualizar todas las transiciones que van hacia él
    this.allStates.forEach((state, stateIndex) => {
      // Encontrar todas las transiciones que van HACIA este estado
      const incomingTransitions = this.transitions.filter(t => t.to_state_id === state.id);

      // Asignar el mismo order a todas las transiciones que van hacia este estado
      // El order será: posición_del_estado * 100
      incomingTransitions.forEach(transition => {
        transitionsToUpdate.push({
          id: transition.id,
          order: stateIndex * 100
        });
      });
    });

    // Llamar al backend para guardar el nuevo orden
    if (transitionsToUpdate.length > 0) {
      this.isReordering = true;

      this.stateMachineService.reorderTransitions(transitionsToUpdate).subscribe({
        next: (response) => {
          if (response.success) {
            // Actualizar los valores de order en las transiciones locales (sin recargar)
            transitionsToUpdate.forEach(update => {
              const transition = this.transitions.find(t => t.id === update.id);
              if (transition) {
                transition.order = update.order;
              }
            });

            this.isReordering = false;

            // Mostrar toast de éxito
            this.showSuccessMessage('Estados reordenados correctamente');
          }
        },
        error: (err) => {
          console.error('❌ Error al reordenar estados:', err);
          // Revertir el cambio local si falla
          this.loadTransitions();
          this.isReordering = false;
        }
      });
    }

    this.draggedState = null;
    this.draggedOverState = null;
  }

  onStateDragEnd(event: DragEvent): void {
    this.draggedState = null;
    this.draggedOverState = null;
    this.stopAutoScroll(); // Detener auto-scroll al terminar drag
  }

  isStateDraggedOver(state: any): boolean {
    return this.draggedOverState?.id === state.id;
  }


  showSuccessMessage(message: string): void {
    this.successMessage = message;
    this.showSuccessToast = true;

    // Ocultar después de 3 segundos
    setTimeout(() => {
      this.showSuccessToast = false;
    }, 3000);
  }

  /**
   * Obtiene el nombre de la tarifa para mostrar en acciones de cobro
   */
  getRateNameForAction(transition: any, action: any): string {
    if (action.code !== 'charge_product_rate') {
      return action.name;
    }

    // Buscar el rate_id en los parámetros de la acción
    let rateId: number | null = null;
    
    if (action.pivot?.parameters) {
      try {
        const params = typeof action.pivot.parameters === 'string' 
          ? JSON.parse(action.pivot.parameters) 
          : action.pivot.parameters;
        rateId = params.rate_id;
      } catch (e) {
        console.error('Error parsing action parameters:', e);
      }
    }

    // Si no hay rate_id, devolver el nombre de la acción
    if (!rateId) {
      return action.name;
    }

    // Buscar la tarifa en el producto
    const priceList = transition.product?.price_lists?.find((pl: any) => pl.id === rateId);
    
    if (priceList) {
      return `${action.name}: ${priceList.concept}`;
    }

    return action.name;
  }
}